/**
 * Runtime configuration for the captive portal.
 *
 * Values come from build-time env vars (Vue CLI only inlines VUE_APP_*).
 * Create a `.env.local` to override while developing, e.g.
 *
 *   VUE_APP_FREERADIUS_API_BASE_URL=http://localhost:8080
 */

// freeradius-api. It proxies the WashXpress OTP endpoints, so the browser never
// talks to the WashXpress API directly.
//
// Default is the same-origin `/api` prefix, served by the portal's own ingress.
// This is deliberate: a hotspot client is unauthenticated when it loads this
// page, so it can only reach hosts the Mikrotik walled garden permits. Calling
// a second hostname (freeradiusapi.laundry-you.com) meant every router needed a
// second walled-garden entry, and because that host sits behind Cloudflare its
// anycast addresses rotate, so an IP/dst-host entry could not pin it reliably.
// The request failing produced "cannot reach the service" on the OTP form.
// Staying on the origin the browser already loaded removes both problems, and
// makes the calls same-origin so CORS is not involved either.
//
// Set VUE_APP_FREERADIUS_API_BASE_URL to an absolute URL to point elsewhere.
export const FREERADIUS_API_BASE_URL = (
  process.env.VUE_APP_FREERADIUS_API_BASE_URL || "/api"
).replace(/\/+$/, "");

// Seconds to wait before the "resend OTP" link becomes active again. The API
// returns the authoritative cooldown when a request is rejected; this is only
// the optimistic default after a successful send.
export const OTP_RESEND_SECONDS = Number(process.env.VUE_APP_OTP_RESEND_SECONDS || 60);

// Length of the OTP issued by the WashXpress API.
export const OTP_LENGTH = 6;

// Where the status page sends a user who already has working internet. The
// "back" button used to always return to the Mikrotik login page, which is a
// dead end once you are online.
export const FACEBOOK_PAGE_URL =
  process.env.VUE_APP_FACEBOOK_PAGE_URL || "https://www.facebook.com/WashXpressTH";

// Connectivity probe for the status page.
//
// The status page cannot tell from its own URL whether the client got online:
// it is reachable both from a logged-in client and from one whose login never
// completed. So it fetches an image over HTTPS and treats a successful load as
// proof of real internet access.
//
// HTTPS is the point. The hotspot cannot proxy TLS -- RouterOS dropped the
// `https-redirect` option in 7.5, so an unauthenticated client's HTTPS request
// is simply not answered rather than being bounced to the login page. A plain
// HTTP probe would be intercepted and "succeed" against the portal itself,
// which is exactly the false positive to avoid.
//
// The probe deliberately points at the same host as FACEBOOK_PAGE_URL and the
// host must stay out of the Mikrotik walled garden (see
// washxpress_hotspot/walled-garden.rsc), otherwise it would load even for an
// unauthenticated client. Probing the actual destination also means a failure
// is never misleading: if the page we are about to open is unreachable, we fall
// back to the login link instead of sending the user to a dead tab.
export const CONNECTIVITY_PROBE_URL =
  process.env.VUE_APP_CONNECTIVITY_PROBE_URL || "https://www.facebook.com/favicon.ico";

export const CONNECTIVITY_PROBE_TIMEOUT_MS = Number(
  process.env.VUE_APP_CONNECTIVITY_PROBE_TIMEOUT_MS || 3000
);
